// "Open in Audiotool" — the whole screen, for one seed.
//
// A seed becomes a new Audiotool project: its arrangement, its audio from R2,
// its notes, its mix. The work runs HERE, in the tab, on the same push core the
// Ableton extension runs (@audiotool-core) — there is no server in the middle.
//
// It starts by itself. Landing on this page IS the intent, so there is no
// button to press and, if the user isn't signed in to Audiotool yet, the page
// goes there immediately and carries on when it comes back.
//
// The studio opens from a LINK, never automatically: a window opened after an
// async export is not a user gesture and browsers block it.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, ChevronLeft, ExternalLink, Loader2, RotateCcw, X, XCircle } from "lucide-react";
import type { AuthenticatedClient } from "@audiotool/nexus";
import { Button } from "@web-shared/components/ui/button";
import { useAudiotool } from "@web-shared/hooks/useAudiotool";
import { api, getChannel } from "@web-shared/lib/api";
import {
  describeExport,
  exportSeed,
  loadWebLibrary,
  markPendingExport,
  rememberExport,
  rememberedExport,
  saveWebLibrary,
  takePendingExport,
  webLinkOrigin,
  type ExportResult,
} from "@web-shared/lib/audiotool";
import {
  applyPushEvent,
  NO_PROGRESS,
  settled,
  stageText,
  type ExportProgress,
  type SampleRow,
} from "@web-shared/lib/audiotool-progress";
import { track as trackAnalytics } from "@web-shared/lib/analytics";
import type { SeedDetail } from "@web-shared/lib/types";

type Status = "checking" | "exporting" | "done" | "error" | "cancelled";

export function AudiotoolExportPage() {
  const { id = "" } = useParams();
  const { session, signIn, signOut, redirectUrl } = useAudiotool();
  const seed = useQuery({
    queryKey: ["seed", id],
    queryFn: () => api<SeedDetail>(`/seeds/${encodeURIComponent(id)}`),
    enabled: Boolean(id),
  });

  const [status, setStatus] = useState<Status>("checking");
  const [progress, setProgress] = useState<ExportProgress>(NO_PROGRESS);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [known, setKnown] = useState(() => rememberedExport(getChannel(), id));

  // Cancel is read through a ref, not through `status`: the export closure is
  // created once and would capture the status it started with forever.
  const cancelled = useRef(false);
  const startedFor = useRef<string | null>(null);

  const run = useCallback(
    async (client: AuthenticatedClient, detail: SeedDetail) => {
      cancelled.current = false;
      setStatus("exporting");
      setProgress(NO_PROGRESS);
      setError(null);
      const library = loadWebLibrary();
      trackAnalytics("remix_started", { seedId: detail.seed.id, detail: "audiotool" });
      try {
        const r = await exportSeed(client, detail, {
          library,
          report: (event) => setProgress((p) => applyPushEvent(p, event)),
          ...webLinkOrigin(),
        });
        if (cancelled.current) return;
        rememberExport(getChannel(), detail.seed.id, r);
        setKnown(rememberedExport(getChannel(), detail.seed.id));
        setResult(r);
        setStatus("done");
        trackAnalytics("remix_completed", { seedId: detail.seed.id, detail: "audiotool" });
      } catch (e) {
        if (cancelled.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
      } finally {
        saveWebLibrary(library); // keep whatever did upload, even after a failure
      }
    },
    [],
  );

  const start = useCallback(
    (detail: SeedDetail) => {
      if (session.status === "signed-in") {
        void run(session.client, detail);
        return;
      }
      // Off to Audiotool; back on this page signed in, the effect below resumes.
      markPendingExport(detail.seed.id);
      signIn(window.location.pathname + window.location.search);
    },
    [session, signIn, run],
  );

  // Begin as soon as there is a seed and a verdict on the session — once per
  // seed, so a re-render never starts a second project.
  useEffect(() => {
    const detail = seed.data;
    if (!detail || session.status === "checking") return;
    if (startedFor.current === detail.seed.id) return;
    // Coming back from sign-in with an export waiting is the same start.
    takePendingExport(detail.seed.id);
    startedFor.current = detail.seed.id;
    start(detail);
  }, [seed.data, session.status, start]);

  const detail = seed.data;
  const title = detail?.seed.title;
  const bpm = detail?.seed.bpm ?? null;

  // "Building" has nothing to count — the writer holds the document lock in
  // batches — so the bar creeps through that band rather than sitting still.
  const [creep, setCreep] = useState(0);
  useEffect(() => {
    if (status !== "exporting") return;
    const t = setInterval(() => setCreep((c) => c + 0.4), 300);
    return () => clearInterval(t);
  }, [status]);
  useEffect(() => setCreep(0), [progress.stage]);
  const percent = Math.min(progress.stage === "done" ? 100 : 97, progress.percent + creep);

  const summary = useMemo(() => (result ? describeExport(result) : null), [result]);

  function cancel(): void {
    cancelled.current = true;
    setStatus("cancelled");
  }

  function again(): void {
    if (!detail) return;
    setResult(null);
    start(detail);
  }

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center bg-background p-6">
      {/* Clipped to the viewport: unclipped, the circles widened the page on a phone. */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -left-[10%] -top-[20%] h-[600px] w-[600px] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -right-[10%] h-[500px] w-[500px] rounded-full bg-blue-900/20 blur-[120px]" />
      </div>
      <div className="relative z-10 w-full max-w-lg space-y-6">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold">
            {status === "done"
              ? "Opened in Audiotool"
              : status === "cancelled"
                ? "Export cancelled"
                : status === "error"
                  ? "Export failed"
                  : "Opening in Audiotool"}
          </h1>
          {/* Nothing until the seed loads — never a "This seed" placeholder. */}
          {title && (
            <p className="text-sm text-muted-foreground">
              {title}
              {bpm ? ` · ${bpm} BPM` : ""}
            </p>
          )}
          {session.status === "signed-in" && status === "exporting" && (
            <p className="text-xs text-muted-foreground">as {session.userName}</p>
          )}
        </div>

        {seed.isError && <Line kind="bad">Couldn't load this seed.</Line>}

        {status === "checking" && !seed.isError && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {session.status === "checking" ? "Connecting…" : "Going to Audiotool to sign in…"}
          </p>
        )}

        {status === "exporting" && (
          <div className="space-y-3">
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
                style={{ width: `${Math.max(3, Math.round(percent))}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">{stageText(progress)}</p>
            <SampleList samples={progress.samples} />
            <div className="flex justify-center">
              <Button variant="ghost" size="sm" onClick={cancel}>
                <X className="mr-1 size-3.5" /> Cancel
              </Button>
            </div>
          </div>
        )}

        {status === "done" && result && (
          <div className="space-y-4">
            <p className="flex items-center gap-2 text-sm">
              <CheckCircle className="size-4 text-primary" />
              {summary?.summary}
            </p>
            {summary && summary.notes.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                {summary.notes.slice(0, 6).map((n) => (
                  <li key={n}>{n}</li>
                ))}
                {summary.notes.length > 6 && <li>…and {summary.notes.length - 6} more</li>}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <a
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground"
                href={result.studioUrl}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink className="size-4" /> Open Audiotool Studio
              </a>
              <Button variant="outline" size="sm" onClick={again}>
                <RotateCcw className="mr-1 size-3.5" /> Export again
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              When you're done editing,{" "}
              <Link
                className="underline"
                to={`/upload/audiotool?project=${encodeURIComponent(result.studioUrl)}&parent=${encodeURIComponent(id)}&root=${encodeURIComponent(detail?.seed.rootSeedId ?? id)}`}
              >
                upload your changes back to Seedbed
              </Link>
              .
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-3">
            <Line kind="bad">{error}</Line>
            <Button variant="outline" size="sm" onClick={again}>
              <RotateCcw className="mr-1 size-3.5" /> Try again
            </Button>
          </div>
        )}

        {status === "cancelled" && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={again}>
              Try again
            </Button>
          </div>
        )}

        {/* This browser has opened this seed before — offer that project rather
            than quietly making a second copy of it. */}
        {known && status !== "exporting" && known.studioUrl !== result?.studioUrl && (
          <p className="text-xs text-muted-foreground">
            You opened this seed in Audiotool before —{" "}
            <a className="underline" href={known.studioUrl} target="_blank" rel="noreferrer">
              that project is still there ↗
            </a>
            .
          </p>
        )}

        {session.status === "signed-out" && session.error && (
          <Line kind="bad">Audiotool sign-in: {session.error}</Line>
        )}

        {session.status === "signed-out" && (
          <p className="text-xs text-muted-foreground">
            Signing in goes to Audiotool and comes back here. If Audiotool says the redirect_uri doesn't match,{" "}
            <code>{redirectUrl}</code> needs adding to the Seedbed app's redirect URIs at developer.audiotool.com.
          </p>
        )}

        <div className="flex items-center justify-between pt-2">
          <Link
            to={`/seeds/${encodeURIComponent(id)}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="size-3.5" /> back to the seed
          </Link>
          {session.status === "signed-in" && status !== "exporting" && (
            <button className="text-xs text-muted-foreground hover:text-foreground" onClick={signOut}>
              sign out of Audiotool
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** One row per sample, in the order the push met them. */
function SampleList({ samples }: { samples: SampleRow[] }) {
  if (samples.length === 0) return null;
  return (
    <div className="max-h-48 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-muted/30">
      {samples.map((s) => (
        <div key={s.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
          {settled(s.state) ? (
            <CheckCircle className={`size-3.5 shrink-0 ${s.state === "uploaded" || s.state === "reused" ? "text-primary" : "text-muted-foreground"}`} />
          ) : (
            <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
          )}
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{s.displayName}</span>
          {s.uses > 1 && <span className="shrink-0 text-muted-foreground">×{s.uses}</span>}
          {s.bytes > 0 && <span className="shrink-0 text-muted-foreground">{Math.round(s.bytes / 1024)} KB</span>}
          <span className="w-16 shrink-0 text-right italic text-muted-foreground">{label(s)}</span>
        </div>
      ))}
    </div>
  );
}

function label(s: SampleRow): string {
  switch (s.state) {
    case "downloading":
      return "fetching";
    case "downloaded":
      return "ready";
    case "uploading":
      return "uploading";
    case "uploaded":
      return "uploaded";
    case "reused":
      return "reused";
    case "missing":
      return "no audio";
    case "failed":
      return "failed";
  }
}

function Line({ kind, children }: { kind: "bad"; children: React.ReactNode }) {
  return (
    <p className={`flex items-start gap-2 text-sm ${kind === "bad" ? "text-[var(--removed)]" : ""}`}>
      <XCircle className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
